import React, { useState, useEffect } from 'react'
import {
  Typography,
  Card,
  Table,
  Tag,
  Button,
  Space,
  message,
  Modal,
  Form,
  Select,
  InputNumber,
  Statistic,
  Row,
  Col,
  Tabs,
  Alert,
  Progress,
  Tooltip,
  Divider,
  Empty,
  Spin
} from 'antd'
import {
  ExperimentOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  PlayCircleOutlined,
  HistoryOutlined,
  LineChartOutlined,
  AlertOutlined,
  SyncOutlined,
  InfoCircleOutlined
} from '@ant-design/icons'
import { validationAPI } from '../services/api'

const { Title, Text } = Typography

const ModelValidation = () => {
  const [loading, setLoading] = useState(false)
  const [summary, setSummary] = useState(null)
  const [walkForwardResults, setWalkForwardResults] = useState([])
  const [predictionStats, setPredictionStats] = useState([])
  const [alerts, setAlerts] = useState([])
  const [thresholds, setThresholds] = useState([])
  const [activeTab, setActiveTab] = useState('overview')
  const [runValidationModal, setRunValidationModal] = useState(false)
  const [runningValidation, setRunningValidation] = useState(false)
  const [runningBackfill, setRunningBackfill] = useState(false)
  const [form] = Form.useForm()
  const isMobile = window.innerWidth < 768

  useEffect(() => {
    fetchAllData()
  }, [])

  const fetchAllData = async () => {
    setLoading(true)
    try {
      const [summaryData, wfData, predData, alertData, thresholdData] = await Promise.all([
        validationAPI.getSummary().catch(e => ({ error: e })),
        validationAPI.getWalkForwardResults({ limit: 50 }).catch(e => []),
        validationAPI.getPredictionStats().catch(e => []),
        validationAPI.getAlerts().catch(e => []),
        validationAPI.getThresholds().catch(e => ({ thresholds: [] }))
      ])

      if (!summaryData.error) setSummary(summaryData)
      setWalkForwardResults(wfData)
      setPredictionStats(predData)
      setAlerts(alertData)
      setThresholds(thresholdData.thresholds || [])
    } catch (error) {
      console.error('Failed to fetch validation data:', error)
      message.error('Failed to load validation data')
    } finally {
      setLoading(false)
    }
  }

  const handleRunValidation = async (values) => {
    setRunningValidation(true)
    try {
      const result = await validationAPI.runValidation(values.model_name, values.months)
      if (result.success) {
        message.success(`Validation complete for ${values.model_name}`)
        Modal.info({
          title: 'Validation Results',
          content: (
            <div>
              <p><strong>Model:</strong> {result.model_name}</p>
              {result.result && (
                <>
                  <p><strong>Spearman IC:</strong> {result.result.spearman_ic?.toFixed(4)}</p>
                  <p><strong>Hit Rate:</strong> {(result.result.hit_rate * 100).toFixed(1)}%</p>
                  <p><strong>L/S Spread:</strong> {(result.result.long_short_spread * 100).toFixed(2)}%</p>
                  <p><strong>Sharpe Ratio:</strong> {result.result.sharpe_ratio?.toFixed(2) || 'N/A'}</p>
                  <p><strong>Test Samples:</strong> {result.result.n_test_samples?.toLocaleString()}</p>
                </>
              )}
            </div>
          ),
          okText: 'Close'
        })
        fetchAllData()
      } else {
        message.warning(result.message)
      }
      setRunValidationModal(false)
      form.resetFields()
    } catch (error) {
      console.error('Validation failed:', error)
      message.error(`Validation failed: ${error.response?.data?.detail || error.message}`)
    } finally {
      setRunningValidation(false)
    }
  }

  const handleBackfill = async () => {
    setRunningBackfill(true)
    try {
      const result = await validationAPI.triggerBackfill(30)
      message.success(`Backfilled ${result.predictions_updated} predictions`)
      fetchAllData()
    } catch (error) {
      console.error('Backfill failed:', error)
      message.error(`Backfill failed: ${error.response?.data?.detail || error.message}`)
    } finally {
      setRunningBackfill(false)
    }
  }

  const getHealthStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'healthy': return 'green'
      case 'warning': return 'orange'
      case 'critical': return 'red'
      default: return 'default'
    }
  }

  const getHealthStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'healthy': return <CheckCircleOutlined />
      case 'warning': return <WarningOutlined />
      case 'critical': return <CloseCircleOutlined />
      default: return <InfoCircleOutlined />
    }
  }

  // Model health columns
  const healthColumns = [
    {
      title: 'Model',
      dataIndex: 'model_name',
      key: 'model_name',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Status',
      dataIndex: 'health_status',
      key: 'health_status',
      render: (status) => (
        <Tag color={getHealthStatusColor(status)} icon={getHealthStatusIcon(status)}>
          {status?.toUpperCase() || 'UNKNOWN'}
        </Tag>
      )
    },
    {
      title: 'IC (7d)',
      dataIndex: 'rolling_ic_7d',
      key: 'rolling_ic_7d',
      render: (val) => val != null ? (
        <span style={{ color: val >= 0.03 ? '#52c41a' : val >= 0.02 ? '#faad14' : '#f5222d' }}>
          {val.toFixed(4)}
        </span>
      ) : 'N/A'
    },
    {
      title: 'IC (30d)',
      dataIndex: 'rolling_ic_30d',
      key: 'rolling_ic_30d',
      render: (val) => val != null ? (
        <span style={{ color: val >= 0.03 ? '#52c41a' : val >= 0.02 ? '#faad14' : '#f5222d' }}>
          {val.toFixed(4)}
        </span>
      ) : 'N/A'
    },
    {
      title: 'Hit Rate (7d)',
      dataIndex: 'rolling_hit_rate_7d',
      key: 'rolling_hit_rate_7d',
      render: (val) => val != null ? (
        <span style={{ color: val >= 0.52 ? '#52c41a' : val >= 0.50 ? '#faad14' : '#f5222d' }}>
          {(val * 100).toFixed(1)}%
        </span>
      ) : 'N/A'
    },
    {
      title: 'Hit Rate (30d)',
      dataIndex: 'rolling_hit_rate_30d',
      key: 'rolling_hit_rate_30d',
      render: (val) => val != null ? (
        <span style={{ color: val >= 0.52 ? '#52c41a' : val >= 0.50 ? '#faad14' : '#f5222d' }}>
          {(val * 100).toFixed(1)}%
        </span>
      ) : 'N/A'
    },
    {
      title: 'Last Updated',
      dataIndex: 'last_updated',
      key: 'last_updated',
      render: (date) => date || 'N/A'
    }
  ]

  // Walk-forward results columns
  const wfColumns = [
    {
      title: 'Model',
      dataIndex: 'model_name',
      key: 'model_name',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Run Date',
      dataIndex: 'run_date',
      key: 'run_date'
    },
    {
      title: 'Test Period',
      key: 'test_period',
      render: (_, record) => `${record.test_start_date} to ${record.test_end_date}`
    },
    {
      title: 'Spearman IC',
      dataIndex: 'spearman_ic',
      key: 'spearman_ic',
      render: (val) => (
        <Tag color={val >= 0.03 ? 'green' : val >= 0.02 ? 'orange' : 'red'}>
          {val?.toFixed(4)}
        </Tag>
      )
    },
    {
      title: 'Hit Rate',
      dataIndex: 'hit_rate',
      key: 'hit_rate',
      render: (val) => (
        <Tag color={val >= 0.52 ? 'green' : val >= 0.50 ? 'orange' : 'red'}>
          {(val * 100).toFixed(1)}%
        </Tag>
      )
    },
    {
      title: 'L/S Spread',
      dataIndex: 'long_short_spread',
      key: 'long_short_spread',
      render: (val) => (
        <Tag color={val >= 0.02 ? 'green' : val >= 0.01 ? 'orange' : 'red'}>
          {(val * 100).toFixed(2)}%
        </Tag>
      )
    },
    {
      title: 'Sharpe',
      dataIndex: 'sharpe_ratio',
      key: 'sharpe_ratio',
      render: (val) => val?.toFixed(2) || 'N/A'
    },
    {
      title: 'Max DD',
      dataIndex: 'max_drawdown',
      key: 'max_drawdown',
      render: (val) => val ? (
        <span style={{ color: '#f5222d' }}>
          {(val * 100).toFixed(1)}%
        </span>
      ) : 'N/A'
    },
    {
      title: 'Samples',
      key: 'samples',
      render: (_, record) => `${record.n_test_samples?.toLocaleString()}`
    }
  ]

  // Prediction stats columns
  const predColumns = [
    {
      title: 'Model',
      dataIndex: 'model_name',
      key: 'model_name',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Total Predictions',
      dataIndex: 'total_predictions',
      key: 'total_predictions',
      render: (val) => val?.toLocaleString()
    },
    {
      title: 'With Actuals',
      dataIndex: 'with_actuals',
      key: 'with_actuals',
      render: (val, record) => (
        <span>
          {val?.toLocaleString()} ({record.total_predictions > 0 ? ((val / record.total_predictions) * 100).toFixed(1) : 0}%)
        </span>
      )
    },
    {
      title: 'Pending Backfill',
      dataIndex: 'pending_backfill',
      key: 'pending_backfill',
      render: (val) => (
        <Tag color={val > 0 ? 'orange' : 'green'}>
          {val?.toLocaleString()}
        </Tag>
      )
    },
    {
      title: 'Accuracy',
      dataIndex: 'overall_accuracy',
      key: 'overall_accuracy',
      render: (val) => val != null ? (
        <Tag color={val >= 0.52 ? 'green' : val >= 0.50 ? 'orange' : 'red'}>
          {(val * 100).toFixed(1)}%
        </Tag>
      ) : 'N/A'
    },
    {
      title: 'First Prediction',
      dataIndex: 'first_prediction',
      key: 'first_prediction'
    },
    {
      title: 'Last Prediction',
      dataIndex: 'last_prediction',
      key: 'last_prediction'
    }
  ]

  const modelOptions = [
    { value: 'dividend_strategy', label: 'Dividend Strategy' },
    { value: 'adaptive_strategy', label: 'Adaptive Strategy' },
    { value: 'value_strategy', label: 'Value Strategy' },
    { value: 'growth_strategy', label: 'Growth Strategy' },
    { value: 'value_largecap_strategy', label: 'Value Large-Cap' },
    { value: 'growth_largecap_strategy', label: 'Growth Large-Cap' },
    { value: 'value_smallcap_strategy', label: 'Value Small-Cap' },
    { value: 'growth_smallcap_strategy', label: 'Growth Small-Cap' }
  ]

  const tabItems = [
    {
      key: 'overview',
      label: (
        <span>
          <LineChartOutlined /> Overview
        </span>
      ),
      children: (
        <>
          {/* Summary Stats */}
          <Row gutter={[16, 16]} className="mb-6">
            <Col xs={12} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Models"
                  value={summary?.total_models || 0}
                  prefix={<ExperimentOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Healthy"
                  value={summary?.healthy_models || 0}
                  valueStyle={{ color: '#52c41a' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Warning"
                  value={summary?.warning_models || 0}
                  valueStyle={{ color: '#faad14' }}
                  prefix={<WarningOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Critical"
                  value={summary?.critical_models || 0}
                  valueStyle={{ color: '#f5222d' }}
                  prefix={<CloseCircleOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* Alerts */}
          {alerts.length > 0 && (
            <Card className="mb-6" title={<span><AlertOutlined /> Active Alerts</span>}>
              {alerts.map((alert, idx) => (
                <Alert
                  key={idx}
                  type={alert.severity === 'critical' ? 'error' : 'warning'}
                  message={`${alert.model_name} - ${alert.severity.toUpperCase()}`}
                  description={alert.degradation_reason}
                  showIcon
                  className="mb-2"
                />
              ))}
            </Card>
          )}

          {/* Model Health Table */}
          <Card title="Model Health Status" className="mb-6">
            {summary?.models?.length > 0 ? (
              <Table
                columns={isMobile ? healthColumns.filter(c => ['model_name', 'health_status', 'rolling_ic_30d'].includes(c.key)) : healthColumns}
                dataSource={summary.models}
                rowKey="model_name"
                pagination={false}
                size="small"
                scroll={{ x: isMobile ? 600 : undefined }}
              />
            ) : (
              <Empty description="No model health data available. Run daily monitoring to populate." />
            )}
          </Card>

          {/* Prediction Summary */}
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Predictions Logged"
                  value={summary?.total_predictions_logged || 0}
                  prefix={<HistoryOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Backfilled"
                  value={summary?.total_backfilled || 0}
                  suffix={summary?.total_predictions_logged > 0 ? `(${((summary.total_backfilled / summary.total_predictions_logged) * 100).toFixed(0)}%)` : ''}
                />
              </Card>
            </Col>
            <Col xs={24} sm={24} lg={12}>
              <Card>
                <Statistic
                  title="Last Walk-Forward Run"
                  value={summary?.last_walk_forward_run || 'Never'}
                  prefix={<PlayCircleOutlined />}
                />
              </Card>
            </Col>
          </Row>
        </>
      )
    },
    {
      key: 'walk-forward',
      label: (
        <span>
          <PlayCircleOutlined /> Walk-Forward Results
        </span>
      ),
      children: (
        <Card>
          {walkForwardResults.length > 0 ? (
            <Table
              columns={isMobile ? wfColumns.filter(c => ['model_name', 'run_date', 'spearman_ic', 'hit_rate'].includes(c.key)) : wfColumns}
              dataSource={walkForwardResults}
              rowKey="id"
              pagination={{ pageSize: 20, showSizeChanger: true }}
              size="small"
              scroll={{ x: isMobile ? 800 : undefined }}
            />
          ) : (
            <Empty description="No walk-forward validation results yet. Run validation during nightly training." />
          )}
        </Card>
      )
    },
    {
      key: 'predictions',
      label: (
        <span>
          <HistoryOutlined /> Prediction Tracking
        </span>
      ),
      children: (
        <Card>
          {predictionStats.length > 0 ? (
            <Table
              columns={isMobile ? predColumns.filter(c => ['model_name', 'total_predictions', 'overall_accuracy'].includes(c.key)) : predColumns}
              dataSource={predictionStats}
              rowKey="model_name"
              pagination={false}
              size="small"
              scroll={{ x: isMobile ? 600 : undefined }}
            />
          ) : (
            <Empty description="No prediction data logged yet. Predictions are logged during portfolio rebalancing." />
          )}
        </Card>
      )
    },
    {
      key: 'thresholds',
      label: (
        <span>
          <InfoCircleOutlined /> Thresholds
        </span>
      ),
      children: (
        <Card>
          <Table
            columns={[
              { title: 'Metric', dataIndex: 'metric_name', key: 'metric_name', render: (t) => <strong>{t}</strong> },
              { title: 'Warning Threshold', dataIndex: 'warning_threshold', key: 'warning', render: (v) => v?.toFixed(4) },
              { title: 'Critical Threshold', dataIndex: 'critical_threshold', key: 'critical', render: (v) => v?.toFixed(4) },
              { title: 'Comparison', dataIndex: 'comparison_operator', key: 'comparison' },
              { title: 'Description', dataIndex: 'description', key: 'description' }
            ]}
            dataSource={thresholds}
            rowKey="metric_name"
            pagination={false}
            size="small"
          />
        </Card>
      )
    }
  ]

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <Title level={2} className="mb-0">
          <ExperimentOutlined className="mr-2" />
          Model Validation
        </Title>
        <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }} size="small" wrap>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchAllData}
            loading={loading}
            block={isMobile}
          >
            Refresh
          </Button>
          <Button
            icon={<SyncOutlined />}
            onClick={handleBackfill}
            loading={runningBackfill}
            block={isMobile}
          >
            Backfill Returns
          </Button>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={() => setRunValidationModal(true)}
            block={isMobile}
          >
            Run Validation
          </Button>
        </Space>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Spin size="large" tip="Loading validation data..." />
        </div>
      ) : (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          size={isMobile ? 'small' : 'default'}
        />
      )}

      {/* Run Validation Modal */}
      <Modal
        title="Run Walk-Forward Validation"
        open={runValidationModal}
        onCancel={() => {
          setRunValidationModal(false)
          form.resetFields()
        }}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleRunValidation}
        >
          <Form.Item
            name="model_name"
            label="Model"
            rules={[{ required: true, message: 'Please select a model' }]}
          >
            <Select placeholder="Select a model to validate" options={modelOptions} />
          </Form.Item>

          <Form.Item
            name="months"
            label="Test Period (months)"
            initialValue={3}
            tooltip="Number of months of test data for walk-forward validation"
          >
            <InputNumber min={1} max={12} style={{ width: '100%' }} />
          </Form.Item>

          <Divider />

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={runningValidation}>
                Run Validation
              </Button>
              <Button onClick={() => {
                setRunValidationModal(false)
                form.resetFields()
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default ModelValidation
